import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import VisibilityIcon from "@mui/icons-material/Visibility";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { displayList } from "@auto/widgets/ListComponent";
import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";
import InlineTags from "@auto/widgets/InlineTags";
import USER_ACCOUNT_REQUEST_TAG_TYPES from "@src/metadata/useraccountrequesttagtypes.mjs";

import styles from "./UserAccountRequestList.module.styl";

import {
  USER_ACCOUNT_REQUEST_LIST_FACETS,
  isDefaultUserAccountRequestListFilters
} from "./UserAccountRequestListFacets";

const LIST_DEFAULT_PAGE_SIZE = 50;

let initialRange = null;
let initialRangeUsesLeft = 0;

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value.replace("T", " ").slice(0, 19);
  }

  try {
    return new Date(value)
      .toISOString()
      .replace("T", " ")
      .slice(0, 19);
  } catch {
    return String(value);
  }
};

const pickFullName = (row) => {
  const firstName = String(
    row.firstName ?? row.first_name ?? ""
  ).trim();

  const lastName = String(
    row.lastName ?? row.last_name ?? ""
  ).trim();

  return [firstName, lastName]
    .filter(Boolean)
    .join(" ");
};

const normalizeTags = (tags) => {
  if (
    !tags ||
    typeof tags !== "object" ||
    Array.isArray(tags)
  ) {
    return {};
  }

  return tags;
};

const renderTags = (row) => {
  const tags = normalizeTags(row.tags);

  if (!Object.keys(tags).length) {
    return null;
  }

  return (
    <InlineTags
      tags={tags}
      tagTypes={USER_ACCOUNT_REQUEST_TAG_TYPES}
      disabled
      showHeader={false}
      emptyText=""
      className={styles.inlineTagsInList}
    />
  );
};

const personImageSrc = (row) => {
  if (!row?.defaultImageId) {
    return "";
  }

  return `${getServiceUri()}person-photo/data/${Number(
    row.defaultImageId
  )}`;
};

const renderPicture = (row, gui) => {
  const src = can(gui, "READ_PERSON_PHOTO") ? personImageSrc(row) : "";

  if (!src) {
    return (
      <AccountCircleIcon
        sx={{
          width: 48,
          height: 48,
          color: "rgba(15, 23, 42, 0.28)"
        }}
      />
    );
  }

  return (
    <img
      src={src}
      alt={row.name || t("person.pictureAlt")}
      style={{
        display: "block",
        width: 48,
        height: 48,
        borderRadius: "50%",
        objectFit: "cover",
        objectPosition: "center",
        background: "#eef2f7",
        boxShadow:
          "0 4px 12px rgba(15, 23, 42, 0.10)"
      }}
    />
  );
};

const readRangeFromUrl = () => {
  const params =
    new URLSearchParams(window.location.search);

  const offset = Number(params.get("offset"));
  const limit = Number(params.get("limit"));

  const safeLimit =
    Number.isFinite(limit) && limit > 0
      ? Math.floor(limit)
      : LIST_DEFAULT_PAGE_SIZE;

  const safeOffset =
    Number.isFinite(offset) && offset >= 0
      ? Math.floor(offset)
      : 0;

  return {
    offset: safeOffset,
    limit: safeLimit,
    page: Math.floor(safeOffset / safeLimit),
    pageSize: safeLimit,
    hasExplicitRange:
      params.has("offset") ||
      params.has("limit")
  };
};

const writeRangeToUrl = ({ offset, limit }) => {
  const url = new URL(window.location.href);

  url.searchParams.set(
    "offset",
    String(offset)
  );

  url.searchParams.set(
    "limit",
    String(limit)
  );

  window.history.replaceState(
    {},
    "",
    url.toString()
  );
};

const getEffectiveQuery = (query) => {
  const search =
    String(query?.search || "").trim();

  const sort =
    Array.isArray(query?.sort)
      ? query.sort
      : [];

  const filters =
    query?.filters || {};

  const hasActiveFilters =
    !isDefaultUserAccountRequestListFilters(
      filters
    );

  const page =
    Number.isFinite(query?.page)
      ? query.page
      : 0;

  const pageSize =
    Number.isFinite(query?.pageSize)
      ? query.pageSize
      : LIST_DEFAULT_PAGE_SIZE;

  if (
    initialRange &&
    initialRange.hasExplicitRange &&
    initialRangeUsesLeft > 0 &&
    !search &&
    !hasActiveFilters &&
    page === 0 &&
    pageSize === LIST_DEFAULT_PAGE_SIZE
  ) {
    initialRangeUsesLeft -= 1;

    return {
      page: initialRange.page,
      pageSize: initialRange.pageSize,
      offset: initialRange.offset,
      limit: initialRange.limit,
      search,
      sort,
      filters
    };
  }

  initialRangeUsesLeft = 0;

  return {
    page,
    pageSize,
    offset: page * pageSize,
    limit: pageSize,
    search,
    sort,
    filters
  };
};

const selectorsForSortItem = (sortItem) => {
  const column = sortItem?.column;

  if (Array.isArray(column?.sortSelectors)) {
    return column.sortSelectors;
  }

  if (column?.sortSelector) {
    return [column.sortSelector];
  }

  if (sortItem?.field) {
    return [sortItem.field];
  }

  throw new Error(
    "Invalid sort item: missing sort selector"
  );
};

const buildSortPayload = (sort) => {
  const out = [];

  for (const sortItem of sort || []) {
    if (
      sortItem?.direction !== "asc" &&
      sortItem?.direction !== "desc"
    ) {
      throw new Error(
        `Invalid sort direction: ${sortItem?.direction}`
      );
    }

    for (
      const selector
      of selectorsForSortItem(sortItem)
    ) {
      if (!selector) {
        throw new Error(
          `Invalid sort selector for field: ${sortItem?.field}`
        );
      }

      out.push({
        selector,
        direction: sortItem.direction
      });
    }
  }

  return out;
};

const buildPayload = (range) => {
  const sort =
    buildSortPayload(range.sort);

  return {
    search: range.search,
    filters: range.filters,
    limit: range.limit,
    offset: range.offset,
    ...(sort.length ? { sort } : {})
  };
};

const requestStatusLabel = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  const labels = {
    PENDING: "common.pending",
    READY: "common.ready",
    ERROR: "common.error",
    APPROVED: "common.approved",
    REJECTED: "common.rejected"
  };

  return labels[normalized] ? t(labels[normalized]) : String(value || "");
};

const normalizeUserAccountRequest = (row) => {
  return {
    ...row,

    gid:
      row.gid ?? "",

    name:
      pickFullName(row),

    username:
      row.username ?? "",

    nationalId:
      row.nationalId ??
      row.national_id ??
      "",

    requestStatus:
      requestStatusLabel(
        row.requestStatus ??
        row.request_status ??
        ""
      ),

    submittedAtFormatted:
      formatDateTime(
        row.submittedAt ??
        row.submitted_at
      ),

    deviceId:
      row.deviceId ??
      row.device_id ??
      "",

    votingCenterId:
      row.votingCenterId ??
      row.voting_center_id ??
      null,

    votingCenterLabel:
      row.votingCenterLabel ??
      "",

    personGid:
      row.personGid ??
      null,

    defaultImageId:
      row.defaultImageId ??
      null,

    tags:
      normalizeTags(row.tags)
  };
};

const userAccountRequestListProjection = {
  storageKey: "haiti.user-account-request",

  facets:
    USER_ACCOUNT_REQUEST_LIST_FACETS,

  columns: [
    {
      title: "common.picture",
      field: "defaultImageId",
      sorting: false,
      filtering: false,
      render: renderPicture
    },

    {
      title: "common.name",
      field: "name",
      sortSelectors: [
        "lastName",
        "firstName"
      ]
    },

    {
      title: "user.username",
      field: "username",
      sortSelector: "username"
    },

    {
      title: "person.nationalId",
      field: "nationalId",
      sortSelector: "nationalId"
    },

    {
      title: "voter.votingCenter",
      field: "votingCenterLabel",
      sorting: false,
      filtering: false,
      render: (row) =>
        row.votingCenterLabel || null
    },

    {
      title: "common.status",
      field: "requestStatus",
      sortSelector: "requestStatus"
    },

    {
      title: "common.tags",
      field: "tags",
      sorting: false,
      filtering: false,
      render: renderTags
    },

    {
      title: "voter.submittedAt",
      field: "submittedAtFormatted",
      sortSelector: "submittedAt"
    },

    {
      title: "common.deviceId",
      field: "deviceId",
      sortSelector: "deviceId"
    },

    {
      title: "common.actions",
      field: "__actions",
      sorting: false,
      filtering: false
    }
  ],

  actions: (row, gui) => (
    can(gui, "READ_USER_ACCOUNT_REQUEST") ? (
      <Tooltip title={t("common.view")}>
        <IconButton
          size="small"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();

            if (row.gid) {
              gui.goTo(`/user-account-request/${row.gid}`);
            }
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null
  ),

  fetch: async (query) => {
    const range =
      getEffectiveQuery(query);

    writeRangeToUrl({
      offset: range.offset,
      limit: range.limit
    });

    const payload =
      buildPayload(range);

    const [data, totalCount] =
      await Promise.all([
        rest.request(
          getServiceUri() +
            "user-account-request-list/search",
          "POST",
          payload
        ),

        rest.request(
          getServiceUri() +
            "user-account-request-list/count",
          "POST",
          payload
        )
      ]);

    return {
      data:
        (data || []).map(
          normalizeUserAccountRequest
        ),

      totalCount,
      page: range.page
    };
  }
};

export const displayUserAccountRequestList = (
  gui
) => {
  initialRange = readRangeFromUrl();

  initialRangeUsesLeft =
    initialRange.hasExplicitRange
      ? 3
      : 0;

  return displayList(
    gui,
    userAccountRequestListProjection
  );
};

export default displayUserAccountRequestList;